import { createSelector, Selector } from '@ngxs/store';
import { DeelnemersState } from '@store/states/deelnemers.state';
import { DeelnemersStateModel } from '@store/models/deelnemers.model';
import { OnderwijsLocatiesState } from '@store/states/onderwijs-locaties.state';
import { OnderwijsLocatiesStateModel } from '@store/models/onderwijs-locaties.model';
import { OnderwijsLocatiesSelectors } from '@store/selectors/onderwijs-locaties.selectors';
import { DeelnemerI } from '@models/deelnemer.model';
import { ProductKey } from '@models/startcode.model';

export class DeelnemersSelectors {
  static getDeelnemersForAntwoordbladen(businessKeyPrefix: string) {
    return createSelector(
      [DeelnemersState, OnderwijsLocatiesState],
      (state: DeelnemersStateModel, onderwijsLocaties: OnderwijsLocatiesStateModel) => {
        return state.deelnemers.filter(
          (deelnemer) =>
            deelnemer.onderwijslocatieId === onderwijsLocaties.selectedOnderwijsLocatieId &&
            deelnemer.planning?.some(p => p.toetsBusinessKey?.includes(businessKeyPrefix)),
        );
      },
    );
  }

  @Selector([DeelnemersState])
  static deelnemers(state: DeelnemersStateModel) {
    return state.deelnemers;
  }

  @Selector([DeelnemersState])
  static deelnemersLoading(state: DeelnemersStateModel) {
    return state.loading;
  }

  @Selector([DeelnemersState])
  static deelnemersError(state: DeelnemersStateModel) {
    return state.error;
  }

  @Selector([DeelnemersState])
  static deelnemersLoaded(state: DeelnemersStateModel) {
    return state.loaded;
  }

  @Selector([DeelnemersState])
  static deelnemersCached(state: DeelnemersStateModel) {
    return state.cached;
  }

  @Selector([DeelnemersState])
  static deelnemerStateStatus(state: DeelnemersStateModel) {
    return {
      cached: state.cached,
      loaded: state.loaded,
      loading: state.loading,
      error: state.error,
    };
  }

  @Selector([DeelnemersSelectors.deelnemers, OnderwijsLocatiesSelectors.selectedOnderwijsLocatieId])
  static getDeelnemersOnLocationId(deelnemers: DeelnemerI[], selectedOnderwijsLocatieId: string) {
    return (deelnemers ?? []).filter(
      (deelnemer) => deelnemer.onderwijslocatieId === selectedOnderwijsLocatieId,
    );
  }

  @Selector([DeelnemersState, OnderwijsLocatiesState])
  static getDeelnemersOnLocationIdWithStartcodeAndPlanning(
    state: DeelnemersStateModel,
    onderwijsLocaties: OnderwijsLocatiesStateModel,
  ) {
    return (state?.deelnemers ?? []).filter(
      (deelnemer) =>
        deelnemer.onderwijslocatieId === onderwijsLocaties?.selectedOnderwijsLocatieId &&
        deelnemer.startcode?.startcode &&
        deelnemer.planning?.some(p => !!p.toetsBusinessKey),
    );
  }

  @Selector([DeelnemersState, OnderwijsLocatiesState])
  static getDeelnemersOnLocationIdWithPlanning(
    state: DeelnemersStateModel,
    onderwijsLocaties: OnderwijsLocatiesStateModel,
  ) {
    return state.deelnemers.filter(
      (deelnemer) =>
        deelnemer.onderwijslocatieId === onderwijsLocaties.selectedOnderwijsLocatieId &&
        deelnemer.planning?.some(p => !!p.toetsBusinessKey),
    );
  }

  @Selector([DeelnemersState])
  static getSelectedDeelnemer(state: DeelnemersStateModel) {
    return state.deelnemers.filter((d) => d.persoonId === state.selectedDeelnemerId)[0];
  }

  @Selector([DeelnemersState])
  static selectedGroep(state: DeelnemersStateModel) {
    return state.selectedGroep;
  }

  @Selector([DeelnemersState, OnderwijsLocatiesState])
  static getGroepenDistinctFromDeelnemers(state: DeelnemersStateModel, onderwijsLocaties: OnderwijsLocatiesStateModel) {
    const distinctGroupNames = state.deelnemers
      .filter((deelnemer) => deelnemer.onderwijslocatieId === onderwijsLocaties.selectedOnderwijsLocatieId)
      .map((item) => item.afnamegroep)
      .filter((name, index, currentVal) => {
        const currentIndex = currentVal.findIndex((v) => v?.groepId === name?.groepId && v?.groep === name?.groep);
        return currentIndex === index;
      });

    return distinctGroupNames;
  }

  @Selector([DeelnemersState, OnderwijsLocatiesState])
  static getGroepenDistinctFromDeelnemersAsCheckboxes(
    state: DeelnemersStateModel,
    onderwijsLocaties: OnderwijsLocatiesStateModel,
  ) {
    const distinctGroupNames = state.deelnemers
      .filter((deelnemer) => deelnemer.onderwijslocatieId === onderwijsLocaties.selectedOnderwijsLocatieId)
      .map((item) => item.afnamegroep)
      .filter((name, index, currentVal) => {
        const currentIndex = currentVal.findIndex((v) => v?.groepId === name?.groepId && v?.groep === name?.groep);
        return currentIndex === index;
      });

    const returnArray = distinctGroupNames.map((value) => {
      return {
        label: value?.groep,
        value: value?.groepId,
      };
    });
    returnArray.unshift({ label: 'Alle groepen', value: '' });
    return returnArray;
  }

  @Selector([DeelnemersState])
  static getSelectedDeelnemersIds(state: DeelnemersStateModel) {
    return state.selectedDeelnemersIds;
  }

  @Selector([DeelnemersState, OnderwijsLocatiesState])
  static getSelectedDeelnemersOnLocationId(
    state: DeelnemersStateModel,
    onderwijsLocaties: OnderwijsLocatiesStateModel,
  ) {
    if (state.selectedDeelnemersIds.length > 0) {
      return state.deelnemers.filter(
        (deelnemer) =>
          state.selectedDeelnemersIds.includes(deelnemer.persoonId) &&
          deelnemer.onderwijslocatieId === onderwijsLocaties.selectedOnderwijsLocatieId,
      );
    }
    return [];
  }

  @Selector([DeelnemersState])
  static creatingStartcodes(state: DeelnemersStateModel) {
    return state.creatingStartcodes;
  }

  @Selector([DeelnemersState])
  static printStartcodes(state: DeelnemersStateModel): boolean {
    return state.printStartcodes;
  }

  @Selector([DeelnemersState, OnderwijsLocatiesState])
  static countStartcodesOnLocationId(
    state: DeelnemersStateModel,
    onderwijsLocaties: OnderwijsLocatiesStateModel,
  ): number {
    return state.deelnemers.filter(
      (deelnemer) =>
        deelnemer.onderwijslocatieId === onderwijsLocaties.selectedOnderwijsLocatieId &&
        deelnemer.startcode?.startcode &&
        deelnemer.planning?.some(p => !!p.toetsBusinessKey),
    ).length;
  }

  @Selector([DeelnemersState])
  static updatingPlanningen(state: DeelnemersStateModel) {
    return state.updatingPlanningen;
  }

  @Selector([DeelnemersState, OnderwijsLocatiesState])
  static getDeelnemersRapportageAvailable(
    state: DeelnemersStateModel,
    onderwijsLocaties: OnderwijsLocatiesStateModel,
  ): DeelnemerI[] {
    return state.deelnemers.filter(
      (deelnemer) =>
        deelnemer.onderwijslocatieId === onderwijsLocaties.selectedOnderwijsLocatieId &&
        deelnemer.rapport?.leerlingRapportAvailable,
    );
  }

  @Selector([DeelnemersState])
  static startcodeProductKeys(state: DeelnemersStateModel): ProductKey[] | null {
    return state.startcodeProductKeys;
  }
}
